import { useEffect, useState, ReactNode, useContext } from "react";
import { useLocation, NavLink } from "react-router-dom";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import SidenavCollapse from "parts/Sidenav/SidenavCollapse";
import SidenavList from "parts/Sidenav/SidenavList";
import SidenavItem from "parts/Sidenav/SidenavItem";
import SidenavRoot from "parts/Sidenav/SidenavRoot";
import sidenavLogoLabel from "parts/Sidenav/styles/sidenav";
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
  LanguageContext,
} from "context";

interface Props {
  color?: "primary" | "secondary" | "info" | "success" | "warning" | "error" | "dark";
  brand?: string;
  brandName: string;
  routes: any[];
  [key: string]: any;
}

function Sidenav({ color = "info", brand = "", brandName, routes, ...rest }: Props): JSX.Element {
  const [openCollapse, setOpenCollapse] = useState<boolean | string>(false);
  const [openNestedCollapse, setOpenNestedCollapse] = useState<boolean | string>(false);
  const [activeKeys, setActiveKeys] = useState<{ activeItemKey: string | null; parentKey: string | null }>({
    activeItemKey: null,
    parentKey: null,
  });

  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } = controller;
  const { pathname } = useLocation();
  const { t } = useContext(LanguageContext) as any;

  const isMobile = () => window.innerWidth < 1200;

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  let textColor: any = "white";
  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }
  
  const findActiveKeys = (routes: any[], pathname: string) => {
    let activeItemKey: string | null = null;
    let parentKey: string | null = null;

    const search = (items: any[], parent: string | null = null): boolean => {
      for (const item of items) {
        if (item.route === pathname) {
          activeItemKey = item.key;
          parentKey = parent;
          return true;
        }
        if (item.collapse && Array.isArray(item.collapse)) {
          if (search(item.collapse, item.key)) return true;
        }
      }
      return false;
    };

    search(routes);
    return { activeItemKey, parentKey };
  };

  useEffect(() => {
    const keys = findActiveKeys(routes, pathname);
    setActiveKeys(keys);
    setOpenCollapse(keys.parentKey || false);
    setOpenNestedCollapse(keys.parentKey || false);
  }, [pathname]);

  useEffect(() => {
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
      setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
    }

    window.addEventListener("resize", handleMiniSidenav);
    handleMiniSidenav();
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, transparentSidenav, whiteSidenav]);

  const renderNestedCollapse = (collapse: any) =>
    collapse.map(({ name, route, key, href, translationKey }: any) => {
      // translationKeyがあれば翻訳、なければ元のnameを使用
      const displayName = translationKey ? t(translationKey) : name;

      return href ? (
        <Link key={key} href={href} target="_blank" rel="noreferrer" sx={{ textDecoration: "none" }}>
          <SidenavItem name={displayName} nested />
        </Link>
      ) : (
        <NavLink
          to={route}
          key={key}
          style={{ textDecoration: "none" }}
          onClick={() => isMobile() && closeSidenav()}
        >
          <SidenavItem name={displayName} active={key === activeKeys.activeItemKey} nested />
        </NavLink>
      );
    });

  const renderCollapse = (collapses: any) =>
    collapses.map(({ name, collapse, route, href, key, translationKey }: any) => {
      const isActive = key === activeKeys.activeItemKey;
      const isParent = key === activeKeys.parentKey;
      
      // translationKeyがあれば翻訳、なければ元のnameを使用
      const displayName = translationKey ? t(translationKey) : name;

      let returnValue;
      if (collapse) {
        returnValue = (
          <SidenavItem
            key={key}
            color={color}
            name={displayName}
            active={isActive || isParent}
            open={openNestedCollapse === key}
            onClick={({ currentTarget }: any) =>
              openNestedCollapse === key && currentTarget.classList.contains("MuiListItem-root")
                ? setOpenNestedCollapse(false)
                : setOpenNestedCollapse(key)
            }
          >
            {renderNestedCollapse(collapse)}
          </SidenavItem>
        );
      } else {
        returnValue = href ? (
          <Link href={href} key={key} target="_blank" rel="noreferrer" sx={{ textDecoration: "none" }}>
            <SidenavItem color={color} name={displayName} active={isActive} />
          </Link>
        ) : (
          <NavLink
            to={route}
            key={key}
            style={{ textDecoration: "none" }}
            onClick={() => isMobile() && closeSidenav()}
          >
            <SidenavItem color={color} name={displayName} active={isActive} />
          </NavLink>
        );
      }
      return <SidenavList key={key}>{returnValue}</SidenavList>;
    });

  const renderRoutes = routes.map(({ type, name, icon, title, collapse, noCollapse, key, href, route, translationKey }: any) => {
    const isActive = key === activeKeys.activeItemKey;
    const isParent = key === activeKeys.parentKey;

    // translationKeyがあれば翻訳、なければ元のname/titleを使用
    const displayName = translationKey ? t(translationKey) : name;
    const displayTitle = translationKey ? t(translationKey) : title;

    if (type === "collapse") {
      if (href) {
        return (
          <Link href={href} key={key} target="_blank" rel="noreferrer" sx={{ textDecoration: "none" }}>
            <SidenavCollapse name={displayName} icon={icon} active={isParent} noCollapse={noCollapse} />
          </Link>
        );
      } else if (noCollapse && route) {
        return (
          <NavLink
            to={route}
            key={key}
            style={{ textDecoration: "none" }}
            onClick={() => isMobile() && closeSidenav()}
          >
            <SidenavCollapse name={displayName} icon={icon} noCollapse={noCollapse} active={isActive} />
          </NavLink>
        );
      } else {
        return (
          <SidenavCollapse
            key={key}
            name={displayName}
            icon={icon}
            active={isParent}
            open={openCollapse === key}
            onClick={() => (openCollapse === key ? setOpenCollapse(false) : setOpenCollapse(key))}
          >
            {collapse ? renderCollapse(collapse) : null}
          </SidenavCollapse>
        );
      }
    } else if (type === "title") {
      return (
        <MDTypography
          key={key}
          color={textColor}
          display="block"
          variant="caption"
          fontWeight="bold"
          textTransform="uppercase"
          pl={3}
          mt={2}
          mb={1}
          ml={1}
        >
          {displayTitle}
        </MDTypography>
      );
    } else if (type === "divider") {
      return (
        <Divider
          key={key}
          light={
            (!darkMode && !whiteSidenav && !transparentSidenav) ||
            (darkMode && !transparentSidenav && whiteSidenav)
          }
        />
      );
    }
    return null;
  });

  return (
    <SidenavRoot {...rest} variant="permanent" ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}>
      <MDBox pt={3} pb={1} px={4} textAlign="center">
        <MDBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <MDTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </MDTypography>
        </MDBox>
        <MDBox component={NavLink} to="/dashboard" display="flex" alignItems="center" onClick={() => isMobile() && closeSidenav()}>
          {brand && <MDBox component="img" src={brand} alt="Chrono Spark" width="4rem" />}
          <MDBox width={!brandName && "100%"} sx={(theme: any) => sidenavLogoLabel(theme, { miniSidenav })}>
            <MDTypography component="h6" variant="button" fontWeight="bold" color={textColor}>
              {brandName}
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox>
      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />
      <List>{renderRoutes}</List>
    </SidenavRoot>
  );
}

export default Sidenav;
